import { Suspense, useState, useEffect } from "react";
import ChatWindow from "../../components/Chat/ChatWindow";
import ImageStudio from "../../components/ImageStudio/ImageStudio";
import { Helmet } from "react-helmet-async";
import { ViewProvider, useView } from "../../context/ViewContext";
import { ImageStudioProvider } from "../../context/ImageStudioContext";
import RootLayout from "../Layout";


const SIDEBAR_COLLAPSED_KEY = "kyva-sidebar-collapsed";

interface HomeBodyProps {
  isSidebarExpanded: boolean;
  setIsSidebarExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

// Renders the shell once; swaps the primary view between chat and studio.
// Both stay mounted (the inactive one is hidden) so switching never loses state.
const HomeBody = ({
  isSidebarExpanded,
  setIsSidebarExpanded,
  isSidebarCollapsed,
  setIsSidebarCollapsed,
}: HomeBodyProps) => {
  const { view } = useView();

  return (
    <RootLayout
      setIsExpanded={setIsSidebarExpanded}
      isExpanded={isSidebarExpanded}
      isCollapsed={isSidebarCollapsed}
      setIsCollapsed={setIsSidebarCollapsed}
    >
      <div className={view === "chat" ? "" : "hidden"}>
        <ChatWindow
          setIsExpanded={setIsSidebarExpanded}
          isExpanded={isSidebarExpanded}
        />
      </div>
      <div className={view === "studio" ? "" : "hidden"}>
        <ImageStudio />
      </div>
    </RootLayout>
  );
};

const Home = () => {
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  });

  useEffect(() => {
    if (window.innerWidth <= 768) {
      setIsSidebarExpanded(false);
    } else {
      setIsSidebarExpanded(true);
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, isSidebarCollapsed ? "1" : "0");
  }, [isSidebarCollapsed]);

  return (
    <div className="bg-surface min-h-screen text-primary">
      <Helmet>
        <title>Chat - Kyva</title>
        <meta name="description" content="Chat with Kyva." />
      </Helmet>
      <Suspense fallback={<div>Loading...</div>}>
        <ViewProvider>
          <ImageStudioProvider>
            <HomeBody
              isSidebarExpanded={isSidebarExpanded}
              setIsSidebarExpanded={setIsSidebarExpanded}
              isSidebarCollapsed={isSidebarCollapsed}
              setIsSidebarCollapsed={setIsSidebarCollapsed}
            />
          </ImageStudioProvider>
        </ViewProvider>
      </Suspense>
    </div>
  );
};

export default Home;
